import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IManufactoringProduction } from "../models/manufactoringProduction";
import { IManufactoringProductionApiResponse } from "../models/api/manufactoringProductionApiResponse";
import { IManufactoringProductionForm } from "../models/forms/manufactoringProductionForm";
import { IManufactoringProductionFilter } from "../models/filters/manufactoringProductionFilter";
import ManufactoringProductionServices from "../services/ManufactoringProductionService";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";
import { IProduction } from "../models/production";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { useProductionStore } from "./production.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useManufactoringProductionStore = defineStore("ManufactoringProduction", () => {
    // State
    const datas = ref<IManufactoringProduction[]>([]);
    const current = ref<IManufactoringProduction>({} as IManufactoringProduction);
    const itemForm = ref<IManufactoringProductionForm>({} as IManufactoringProductionForm);
    const isLoading = ref<boolean>(false);

    // Actions - Hydration
    /**
     * Hydrate ManufactoringProductions from API response
     */
    async function hydrateManufactoringProductions(productions: IManufactoringProductionApiResponse[], centerId?: number, dateFrom?: Date): Promise<IManufactoringProduction[]> {
        if (productions.length === 0) return [];

        // 1. Get stores
        const productionStore = useProductionStore();

        // 2. Load in parallel if needed
        // Si le store est en cours de chargement, attendre qu'il finisse au lieu de skip
        const waitForStore = (store: { isLoading: boolean }) =>
            new Promise<void>(resolve => {
                if (!store.isLoading) return resolve();
                const interval = setInterval(() => {
                    if (!store.isLoading) { clearInterval(interval); resolve(); }
                }, 50);
            });

        const loadPromises: Promise<unknown>[] = [];
        const prodNeedsLoad =
            productionStore.datas.length === 0 ||
            (centerId != null && productionStore.loadedCenterId !== centerId) ||
            (dateFrom === undefined && productionStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && productionStore.loadedDateFrom !== undefined && productionStore.loadedDateFrom > dateFrom);
        if (prodNeedsLoad) {
            if (productionStore.isLoading) {
                loadPromises.push(waitForStore(productionStore));
            } else {
                loadPromises.push(productionStore.load({ page: 1, pageSize: 0, centerId, dateFrom }));
            }
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const productionMap = new Map(productionStore.datas.map(p => [p.id, p]));

        // 4. Hydrate all entities in one pass
        const models: IManufactoringProduction[] = [];
        for (const production of productions) {
            const model = { ...production } as unknown as IManufactoringProduction;

            // Link references (O(1) via Map)
            model.production = productionMap.get(production.productionId) ?? model.production;

            models.push(model);
        }

        return models;
    }

    // Getters
    const getManufactoringById = computed(() => {
        return (id: number): IManufactoringProduction | undefined => {
            // Use productionId instead of production?.id to avoid undefined access
            return datas.value?.find((manufactoringProduction) => manufactoringProduction.productionId == id);
        };
    });

    const todayAndYesterdayManufactures = computed(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        const startOfTomorrow = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

        return datas.value.filter(m => {
            let itemDate = new Date(m.addingDate);
            return itemDate >= startOfYesterday && itemDate < startOfTomorrow;
        });
    });

    // State
    const loadedDateFrom = ref<Date | undefined>(undefined);

    // Actions
    async function load(filter: IManufactoringProductionFilter, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);
        isLoading.value = true;

        // Pré-chargement de productionStore en parallèle avec l'appel API principal.
        // hydrateManufactoringProductions() en a besoin : sans ça, il le charge séquentiellement après le GET.
        const productionStore = useProductionStore();
        const subStorePromises: Promise<unknown>[] = [];
        const prodNeedsLoad =
            productionStore.datas.length === 0 ||
            (filter.centerId != null && productionStore.loadedCenterId !== filter.centerId) ||
            (filter.dateFrom === undefined && productionStore.loadedDateFrom !== undefined) ||
            (filter.dateFrom !== undefined && productionStore.loadedDateFrom !== undefined && productionStore.loadedDateFrom > filter.dateFrom);
        if (!productionStore.isLoading && prodNeedsLoad) {
            subStorePromises.push(productionStore.load({ page: 1, pageSize: 0, centerId: filter.centerId, dateFrom: filter.dateFrom }));
        }

        start('GET /ManufactoringProduction (réseau)');
        const result = await ManufactoringProductionServices.getAll<IManufactoringProductionFilter, IManufactoringProductionApiResponse>(filter);
        end('GET /ManufactoringProduction (réseau)', 'api');

        if (subStorePromises.length > 0) {
            start('Attente productionStore');
            await Promise.all(subStorePromises);
            end('Attente productionStore', 'api');
        }

        datas.value = [];
        if (!result.error) {
            start('hydrateManufactoringProductions()');
            datas.value = await hydrateManufactoringProductions(result.data as IManufactoringProductionApiResponse[], filter.centerId, filter.dateFrom);
            end('hydrateManufactoringProductions()', 'mapping');
        }
        isLoading.value = false;
        loadedDateFrom.value = filter.dateFrom;
        return result;
    }

    async function get(id: number) {
        const result = await ManufactoringProductionServices.getById<IManufactoringProductionApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value);

            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1)
                datas.value[index] = modelData;

            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function getByProduction(id: number) {
        const result = await ManufactoringProductionServices.getByIdOther<IManufactoringProductionApiResponse>(id, "production");
        console.log("Result getByProduction", result);

        if (!result.error && (result.data as IManufactoringProductionApiResponse).id != 0) {
            const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value);

            // Use productionId instead of production.id to avoid undefined access
            const index = datas.value.findIndex((item) => item.productionId === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            } else {
                datas.value.push(modelData);
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<IManufactoringProduction | IDataOperationResult<IManufactoringProduction>>;
        if (itemForm.value.id > 0) {
            result = await ManufactoringProductionServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value);
                current.value = modelData;

                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = "Item non trouvé dans le store";
                    return new ServiceOperationResult<IDataOperationResult<IManufactoringProduction>>(true, { errors: error });
                }
            }
        } else {
            result = await ManufactoringProductionServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateManufactoringProductions([result.data as IManufactoringProductionApiResponse], undefined, loadedDateFrom.value);
                current.value = modelData;
            }
        }
        return result;
    }

    function toForm(item: IManufactoringProduction): IManufactoringProductionForm {
        const form = {} as IManufactoringProductionForm;
        Object.assign(form, item);
        form.productionId = item.productionId;
        itemForm.value = form;
        return form;
    }

    function newForm(lienId: number): IManufactoringProductionForm {
        itemForm.value = {
            id: 0,
            productionId: lienId,
            concentrationFinale: 0,
            volumeAmorce: 0,
            volumeFinal: 0,
            numberFabrication: 0,
            isHetero: false,
        } as IManufactoringProductionForm;
        return itemForm.value;
    }

    function newItem(lien: IProduction): IManufactoringProduction {
        current.value = {
            id: 0,
            production: lien,
            productionId: lien.id,
            concentrationFinale: 0,
            volumeAmorce: 0,
            volumeFinal: 0,
            numberFabrication: 0,
            isHetero: false,
        } as IManufactoringProduction;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        loadedDateFrom,
        // Getters
        getManufactoringById,
        todayAndYesterdayManufactures,
        // Actions
        load,
        get,
        getByProduction,
        save,
        toForm,
        newForm,
        newItem,
        hydrateManufactoringProductions
    };
});

signalRManager.connection.on("MajManufactoringProduction", async (payload: IPayloadStore) => {
    const store = useManufactoringProductionStore();
    // Passer loadedDateFrom pour éviter de déclencher un rechargement sans filtre du productionStore
    const dateFrom = store.loadedDateFrom;
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateManufactoringProductions([payload.data as IManufactoringProductionApiResponse], undefined, dateFrom);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateManufactoringProductions([payload.data as IManufactoringProductionApiResponse], undefined, dateFrom);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
